import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { useLanguage } from "../../context/LanguageContext";
import { ArrowRight, ArrowLeft, Award, Globe, Shield, CheckCircle } from "lucide-react";

// Lynae Product Images
const LYNAE_IMAGES = {
  group1: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/q6goza6r_IMG_6468.jpg",
  group2: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/k6w2pfvj_IMG_6484.jpg",
  vitamin_b12: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/nwegxc9h_IMG_6472.jpg",
  omega_369: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/wo3f24qp_IMG_6473.jpg",
  coq10: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/kr9tnjrz_IMG_6475.jpg",
  selenium: "https://customer-assets.emergentagent.com/job_4dfc9d30-7651-4680-845d-7138c0d12cff/artifacts/pvbauurt_IMG_6476.jpg",
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bahr-rimal-system/artifacts/xdruqubd_IMG_6467.JPG";

const CompaniesPage = () => {
  const { t, isRTL } = useLanguage();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const partners = [
    {
      name: "LYNAE®",
      nameAr: "لينى",
      logo: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop",
      type: "mainDistributor",
      description: isRTL 
        ? "الموزع الرئيسي والحصري لمنتجات لينى للفيتامينات والمكملات الغذائية في ليبيا. منتجات أمريكية عالية الجودة."
        : "Main and exclusive distributor for Lynae Vitamins and Supplements in Libya. High-quality American products.",
      country: "USA",
      products: ["Vitamins", "Minerals", "Supplements", "Omega-3"],
      images: [LYNAE_IMAGES.group1, LYNAE_IMAGES.group2],
    },
  ];

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0f172a] overflow-hidden" data-testid="companies-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-[#d4af37] to-[#38bdf8]" />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Badge className="mb-6 bg-[#d4af37] text-[#0f172a]">{t("ourPartners")}</Badge>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
              {t("trustedPartners")}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              {t("companiesDescription")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lynae Partnership Section */}
      <section className="py-24" data-testid="lynae-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Main Partner Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Card className="overflow-hidden border-2 border-[#d4af37]">
              <CardContent className="p-0">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Info Side */}
                  <div className="p-8 lg:p-12 bg-gradient-to-br from-[#1a365d] to-[#0f172a] text-white">
                    <Badge className="mb-4 bg-[#d4af37] text-[#0f172a]">{t("mainDistributor")}</Badge>
                    <h2 className="font-heading text-4xl font-bold mb-4">LYNAE®</h2>
                    <h3 className="text-2xl text-[#d4af37] mb-6">
                      {isRTL ? "فيتامينات ومكملات غذائية" : "Vitamins & Supplements"}
                    </h3>
                    <p className="text-slate-300 mb-8 leading-relaxed">
                      {partners[0].description}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#d4af37]/20 rounded-lg">
                          <Globe className="w-5 h-5 text-[#d4af37]" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">{isRTL ? "بلد المنشأ" : "Origin"}</p>
                          <p className="font-semibold">{isRTL ? "الولايات المتحدة" : "USA"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#d4af37]/20 rounded-lg">
                          <Award className="w-5 h-5 text-[#d4af37]" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">{isRTL ? "الحالة" : "Status"}</p>
                          <p className="font-semibold">{isRTL ? "موزع حصري" : "Exclusive"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#d4af37]/20 rounded-lg">
                          <Shield className="w-5 h-5 text-[#d4af37]" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">{isRTL ? "الجودة" : "Quality"}</p>
                          <p className="font-semibold">{isRTL ? "معتمد دولياً" : "Certified"}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#d4af37]/20 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-[#d4af37]" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">{isRTL ? "منذ" : "Since"}</p>
                          <p className="font-semibold">2015</p>
                        </div>
                      </div>
                    </div>
                    
                    <Link to="/products?brand=Lynae">
                      <Button className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90">
                        {t("viewLynaeProducts")}
                        <ArrowIcon className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                      </Button>
                    </Link>
                  </div>
                  
                  {/* Image Side */}
                  <div className="bg-slate-100 p-8 lg:p-12">
                    <div className="grid grid-cols-2 gap-4">
                      <motion.img
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        src={LYNAE_IMAGES.group1}
                        alt="Lynae Products"
                        className="rounded-xl shadow-lg w-full h-48 object-cover"
                      />
                      <motion.img
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        viewport={{ once: true }}
                        src={LYNAE_IMAGES.group2}
                        alt="Lynae Products"
                        className="rounded-xl shadow-lg w-full h-48 object-cover"
                      />
                      <motion.img
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                        src={LYNAE_IMAGES.vitamin_b12}
                        alt="Vitamin B-12"
                        className="rounded-xl shadow-lg w-full h-48 object-cover"
                      />
                      <motion.img
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        viewport={{ once: true }}
                        src={LYNAE_IMAGES.coq10}
                        alt="CoQ10"
                        className="rounded-xl shadow-lg w-full h-48 object-cover"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-24 bg-slate-50" data-testid="product-showcase">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">
              {isRTL ? "منتجات لينى المميزة" : "Featured Lynae Products"}
            </h2>
            <p className="text-muted-foreground">
              {isRTL ? "اكتشف مجموعتنا الواسعة من الفيتامينات والمكملات الغذائية" : "Discover our wide range of vitamins and supplements"}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { img: LYNAE_IMAGES.vitamin_b12, name: "Vitamin B-12", nameAr: "فيتامين ب-12" },
              { img: LYNAE_IMAGES.omega_369, name: "Omega 3-6-9", nameAr: "أوميجا 3-6-9" },
              { img: LYNAE_IMAGES.coq10, name: "CoQ10 200mg", nameAr: "كو كيو 10" },
              { img: LYNAE_IMAGES.selenium, name: "Selenium Yeast", nameAr: "سيلينيوم" },
            ].map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="overflow-hidden hover:shadow-xl transition-shadow group">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.img}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-4 text-center">
                    <p className="font-semibold">{isRTL ? product.nameAr : product.name}</p>
                    <p className="text-xs text-[#d4af37]">LYNAE®</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/products">
              <Button size="lg" className="bg-[#1a365d] hover:bg-[#1a365d]/90">
                {t("viewAll")}
                <ArrowIcon className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1a365d]" data-testid="partner-cta">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              {t("partnerWithUs")}
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              {isRTL 
                ? "هل أنت مهتم بالشراكة معنا؟ تواصل معنا لمعرفة المزيد عن فرص التوزيع."
                : "Interested in partnering with us? Contact us to learn more about distribution opportunities."
              }
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90">
                {t("contactUs")}
                <ArrowIcon className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompaniesPage;
